import React from "react";
import { Badge, Box, Button, Drawer, Group, ScrollArea, Text, Title } from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import { Clock, Route, X } from "tabler-icons-react";
import moment from "moment";

function parseStep(step, index) {
  try {
    const messageData = JSON.parse(step.message);
    const routeMatch = messageData.message?.match(/"route":"([^"]+)"/);
    const paramsMatch = messageData.message?.match(/"currentRouteParams":({[^}]+})/);
    let params = {};

    if (paramsMatch) {
      try {
        params = JSON.parse(paramsMatch[1]);
      } catch {
        params = {};
      }
    }

    return {
      id: index,
      route: routeMatch ? routeMatch[1] : "Unknown route",
      params,
      timestamp: step.timestamp || messageData.timestamp,
      level: messageData.level || "info",
      category: messageData.category || "screen",
      originalMessage: messageData.message || step.message,
    };
  } catch {
    return {
      id: index,
      route: "Unknown route",
      params: {},
      timestamp: step.timestamp || Date.now(),
      level: "info",
      category: "screen",
      originalMessage: step.message,
    };
  }
}

function formatTimestamp(timestamp) {
  const numeric = Number(timestamp);
  const date =
    Number.isFinite(numeric) && `${Math.trunc(numeric)}`.length <= 10
      ? moment.unix(numeric)
      : moment(timestamp);
  return date.isValid() ? date.format("HH:mm:ss.SSS") : "-";
}

const UserJourneyDrawer = ({
  opened,
  userSteps = [],
  handleOnClose,
  duration = "0s",
}) => {
  const { height } = useViewportSize();
  const parsedSteps = userSteps.map(parseStep);

  return (
    <Drawer
      opened={opened}
      onClose={handleOnClose}
      size="min(560px, 92vw)"
      position="right"
      overlayOpacity={0.25}
      lockScroll
      withCloseButton={false}
      withinPortal={false}
      classNames={{ drawer: "detail-drawer" }}
    >
      <div className="detail-drawer__header">
        <Group spacing={10} noWrap>
          <div className="app-header__mark">
            <Route size={18} />
          </div>
          <div>
            <Title order={3} className="detail-drawer__title">
              User journey
            </Title>
            <Text className="detail-drawer__meta">
              {parsedSteps.length} steps · {parsedSteps.length > 1 ? duration : "0s"}
            </Text>
          </div>
        </Group>
        <Button compact variant="subtle" className="icon-button" onClick={handleOnClose}>
          <X size={16} />
        </Button>
      </div>

      <ScrollArea style={{ height: height - 86 }}>
        <Box className="journey-body">
          {parsedSteps.length === 0 ? (
            <div className="logs-empty">
              <Route size={18} />
              <Text>No user steps found in this session</Text>
            </div>
          ) : (
            parsedSteps.map((step, index) => (
              <div className="journey-step" key={step.id}>
                <div className="journey-step__rail">
                  <span>{index + 1}</span>
                </div>
                <div className="journey-step__content">
                  <Group position="apart" spacing={10} noWrap>
                    <Group spacing={8} noWrap>
                      <Text className="journey-step__route">{step.route}</Text>
                      <Badge className="compact-badge">{step.category}</Badge>
                    </Group>
                    <Group spacing={5} noWrap className="journey-step__time">
                      <Clock size={13} />
                      <span>{formatTimestamp(step.timestamp)}</span>
                    </Group>
                  </Group>

                  {Object.keys(step.params).length > 0 ? (
                    <div className="journey-step__params">
                      {Object.entries(step.params).map(([key, value]) => (
                        <code key={key}>
                          {key}: {String(value)}
                        </code>
                      ))}
                    </div>
                  ) : null}

                  <pre className="journey-step__raw">{step.originalMessage}</pre>
                </div>
              </div>
            ))
          )}
        </Box>
      </ScrollArea>
    </Drawer>
  );
};

export default UserJourneyDrawer;
